import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth } from "../context/AuthContext";

// Istruzioni d'uso per sezione, differenziate per ruolo.
// Chiavi ruolo: admin | condomino | fornitore | "*" (fallback).
const HINTS = {
  dashboard: {
    admin: "Da qui controlli a colpo d'occhio le manutenzioni: clicca su un riquadro di stato (Nuove, Affidate, In lavorazione…) per aprire l'elenco filtrato. Usa “Nuova manutenzione” per aprire un ticket a nome di un condòmino.",
    condomino: "Questa è la tua home: vedi le tue manutenzioni e il loro stato. Premi “Nuova manutenzione” per segnalare un guasto nel tuo condominio.",
    fornitore: "Qui trovi gli incarichi assegnati a te. Apri un incarico per accettarlo/rifiutarlo e per segnalare l'esecuzione.",
  },
  segnalazioni: {
    admin: "Elenco di tutte le richieste di manutenzione. Filtra per stato/priorità, apri una scheda per affidarla a un fornitore, aggiornarne lo stato e chiuderla con la ricevuta.",
    condomino: "Le manutenzioni che hai inviato. Apri una scheda per vedere lo stato, il fornitore incaricato e, a lavoro concluso, lasciare una valutazione.",
    fornitore: "Gli incarichi che ti sono stati affidati. Usa “Accetto/Rifiuto incarico” e poi “Incarico eseguito” quando hai finito.",
  },
  "nuova-segnalazione": {
    "*": "Compila i passaggi guidati: scegli condominio e tipo di problema, aggiungi una descrizione chiara e (se utile) delle foto. Prima dell'invio controlla se il guasto è già stato segnalato per evitare doppioni.",
  },
  bacheca: {
    admin: "Invia avvisi ai condòmini (a tutti, a interi condomìni o a singoli) con priorità e allegati. Lo storico in basso è filtrabile per condominio; clicca un avviso per rivederlo o reinviarlo.",
    condomino: "Qui leggi gli avvisi pubblicati dallo Studio per il tuo condominio.",
  },
  "comunicazioni-fornitori": {
    admin: "Invia comunicazioni ai fornitori per categoria, condominio o singolarmente. L'invio usa email + notifica in-app. Lo storico mostra il destinatario di ogni messaggio.",
    fornitore: "Le comunicazioni che lo Studio ha inviato a te.",
  },
  condomini: {
    admin: "Anagrafica degli edifici amministrati. Unità immobiliari, proprietari e conduttori sono calcolati dal RAC. Usa “Apri nel RAC” per vedere i dettagli catastali.",
  },
  fornitori: {
    admin: "Anagrafica fornitori. Clicca i riquadri “Attivati”/“Da attivare” per filtrare la lista. Crea account di accesso e invia le credenziali (singolarmente o in blocco). Assegna una categoria ai fornitori che ne sono privi.",
  },
  residenti: {
    admin: "Account dei condòmini. Clicca i riquadri “Attivati”/“Da attivare” per filtrare. L'interruttore in alto decide se le email con le credenziali partono automaticamente. Importa in blocco da Excel o aggiungi manualmente.",
  },
  proprieta: {
    admin: "Registro Anagrafe Condominiale (RAC). Importa da Excel i dati anagrafici e catastali, completa i dati di possesso e genera il PDF del RAC per singola scheda o per intero condominio.",
    condomino: "I dati del tuo immobile. Completa i dati di possesso (atto notorio) e il consenso privacy; resteranno a disposizione dello Studio.",
  },
  sinistri: {
    admin: "Gestione dei sinistri assicurativi: compagnia, polizza, perito, importi e fasi (Aperto → In gestione → Definito).",
  },
  preventivi: {
    admin: "Richiedi preventivi ai fornitori e gestisci le risposte. Aderendo a un preventivo crei automaticamente la manutenzione collegata.",
    fornitore: "Le richieste di preventivo ricevute. Rispondi con importo, note ed eventuale allegato PDF.",
  },
  report: {
    admin: "Report periodici delle manutenzioni. Esporta in Excel/PDF, lancia il controllo alert e invia i manuali d'uso.",
  },
  statistiche: {
    admin: "Indicatori e grafici: interventi, costi, tempi medi e dettaglio per condominio.",
  },
  scadenze: {
    admin: "Calendario mensile delle manutenzioni aperte, colorate per priorità. Naviga tra i mesi o torna a “Oggi”.",
  },
  pannello: {
    admin: "Pannello account: crea collaboratori interni e account di prova (trial) isolati per potenziali clienti. Qui trovi anche la diagnostica email.",
  },
  "login-register": {
    "*": "Due modi per accedere: 1) lo Studio crea il tuo account e ti consegna ID + codice → usa “Prima attivazione account”; 2) registrati qui in autonomia come condòmino. In entrambi i casi scegli tu la password.",
  },
  "login-attivazione": {
    "*": "Usa questa opzione se lo Studio ti ha già consegnato un ID di accesso (es. m.rossi) e un codice di attivazione. Inseriscili una sola volta e scegli la tua password: l'account è pronto.",
  },
  "login-registrazione": {
    "*": "Sei un condòmino e non hai ricevuto credenziali dallo Studio? Registrati qui in autonomia: indica nome, email, condominio e scegli una password per iniziare a inviare manutenzioni.",
  },
  guida: {
    "*": "Qui trovi il manuale d'uso aggiornato per il tuo ruolo, la presentazione guidata dell'app e le istruzioni per installarla sul telefono. Puoi anche scaricare il manuale in PDF.",
  },
};

export function InfoHint({ section, className = "" }) {
  const { user } = useAuth();
  const entry = HINTS[section];
  if (!entry) return null;
  const role = user?.role;
  const text = entry[role] || entry["*"];
  if (!text) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`info-hint-${section}`}
          aria-label="Come si usa questa sezione"
          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-brand bg-[#FBEEF3] border border-[#EBC5D2] hover:bg-[#F0D9E1] transition-colors shrink-0 ${className}`}
        >
          <Info className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-w-sm text-sm leading-relaxed" data-testid={`info-hint-content-${section}`}>
        <div className="font-bold text-brand mb-1.5 flex items-center gap-1.5">
          <Info className="w-4 h-4" /> Come si usa
        </div>
        <p className="text-slate-700 whitespace-pre-line">{text}</p>
      </PopoverContent>
    </Popover>
  );
}
